import AccentColor from "datasets/types/enums/accent-color";
import { AccentPaletteColor, resolveAccentPaletteColor } from "~/lib/accentPalette";

/** Maps legacy dataset accent colors to their closest match in `ACCENT_PALETTE`. */
const LEGACY_ACCENT_MIGRATION: Partial<Record<AccentColor, string>> = {
  [AccentColor.GREY]: "#212121",
  [AccentColor.PURPLE]: "#CC78BC",
  [AccentColor.PINK]: "#CC78BC",
  [AccentColor.GREEN]: "#029E73",
  [AccentColor.ORANGE]: "#DE8F05",
  [AccentColor.BLUE]: "#0173B2",
};

/**
 * Reverse of `LEGACY_ACCENT_MIGRATION`, for legacy pages that only understand
 * `AccentColor` — lets a dataset colored via the new `ACCENT_PALETTE` (e.g. from
 * the nextgen Appearance settings) still show its closest legacy equivalent
 * instead of falling back to the default Purple.
 */
const REVERSE_ACCENT_MIGRATION: Record<string, AccentColor> = {
  "#212121": AccentColor.GREY,
  "#CC78BC": AccentColor.PINK,
  "#029E73": AccentColor.GREEN,
  "#DE8F05": AccentColor.ORANGE,
  "#0173B2": AccentColor.BLUE,
};

/**
 * Returns the dataset's accent color if it's a recognized legacy color, maps it
 * from the new `ACCENT_PALETTE` if applicable, or defaults to Purple.
 */
export const getAccentColor = (color?: string): AccentColor => {
  if (!color) {
    return AccentColor.PURPLE;
  }
  if (Object.values(AccentColor).includes(color as AccentColor)) {
    return color as AccentColor;
  }
  return REVERSE_ACCENT_MIGRATION[color.toUpperCase()] ?? AccentColor.PURPLE;
};

/**
 * Resolves a dataset's persisted `accentColor` to a card palette entry, migrating
 * legacy dataset accent colors to their `ACCENT_PALETTE` equivalent.
 */
export const resolveDatasetAccentColor = (accentColor?: string): AccentPaletteColor =>
  resolveAccentPaletteColor(
    accentColor && accentColor in LEGACY_ACCENT_MIGRATION
      ? LEGACY_ACCENT_MIGRATION[accentColor as AccentColor]
      : accentColor
  );
